/*
 *   File : rpd.js
 *   Description : Digio reverse penny drop (RPD) helpers
 */

import Cookies from 'js-cookie';

import APIService from '../Service/api-service';
import OrderAPIService from '../Service/order-api';

export const ADMIN_RPD_TOKEN_KEY = 'admin_rpd_token';
export const RPD_POLL_PENDING_KEY = 'rpd_poll_pending';

export const setAdminRpdToken = (token) => {
    if (token) {
        sessionStorage.setItem(ADMIN_RPD_TOKEN_KEY, token);
    }
};

export const getAdminRpdToken = () => sessionStorage.getItem(ADMIN_RPD_TOKEN_KEY);

export const clearAdminRpdToken = () => {
    sessionStorage.removeItem(ADMIN_RPD_TOKEN_KEY);
};

export const setRpdPollPending = () => {
    sessionStorage.setItem(RPD_POLL_PENDING_KEY, 'true');
};

export const clearRpdPollPending = () => {
    sessionStorage.removeItem(RPD_POLL_PENDING_KEY);
};

export const isRpdPollPending = () => sessionStorage.getItem(RPD_POLL_PENDING_KEY) === 'true';

export const isAdminRpdReturnRoute = () => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    return path === '/rpd' && params.get('digio_return') === '1' && Boolean(params.get('token'));
};

export const isRpdDigioCallbackRoute = () => {
    const path = window.location.pathname;
    const verificationType = new URLSearchParams(window.location.search).get('verification-type');
    return path.startsWith('/digio-callback') && verificationType === 'rpd';
};

export const shouldSuppressUnlockForRpd = () => {
    return isRpdPollPending() || isRpdDigioCallbackRoute() || isAdminRpdReturnRoute();
};

export const isAdminRpdFlow = () => {
    return isRpdPollPending() || isRpdDigioCallbackRoute() || Boolean(getAdminRpdToken());
};

const getRpdPendingMessage = (response) => {
    const raw = response?.message ?? response?.data ?? '';
    return typeof raw === 'string' ? raw.toLowerCase() : String(raw).toLowerCase();
};

const isRpdTerminalFailure = (response) => {
    if (response?.status_code !== 400) {
        return false;
    }

    const message = getRpdPendingMessage(response);

    return (
        message.includes('failed') ||
        message.includes('rejected') ||
        message.includes('cancelled') ||
        message.includes('invalid') ||
        message.includes('expired') ||
        message.includes('error')
    );
};

const getDigioGatewayBaseUrl = () => {
    if (process.env.REACT_APP_DIGIO_URL) {
        return process.env.REACT_APP_DIGIO_URL.replace(/\/$/, '');
    }

    const isProd = process.env.REACT_APP_DIGILOCKER_ENVIRONMENT === 'production';
    return isProd
        ? 'https://app.digio.in/#/gateway/login'
        : 'https://ext-gateway.digio.in/#/gateway/login';
};

export const parseDigioRpdData = (data) => {
    if (typeof data === 'string' && data.startsWith('http')) {
        return { url: data };
    }

    let payload = data;
    if (payload?.data && typeof payload.data === 'object' && !payload.id) {
        payload = payload.data;
    }

    if (payload?.digio_url || payload?.url) {
        return { url: payload.digio_url || payload.url };
    }

    const digioId = payload?.id || payload?.digio_id || payload?.request_id || payload?.kyc_id;
    const mail = payload?.customer_identifier || payload?.email || payload?.identifier;
    const tokenId = payload?.access_token?.id || payload?.access_token || payload?.token_id || payload?.gateway_token;

    if (!digioId || !mail || !tokenId) {
        return null;
    }

    return { digioId, mail, tokenId };
};

export const initiateRpd = (kycFlow = false) => {
    if (kycFlow === true) {
        return APIService(false, 'kyc/initiate-rpd');
    }

    return APIService(false, 'profile/banks/initiate-rpd');
};

export const buildDigioRpdUrl = (data, options = {}) => {
    const { kycFlow = false, adminToken = null } = options;
    const parsed = parseDigioRpdData(data);

    if (!parsed) {
        throw new Error('Invalid Digio RPD response');
    }

    if (parsed.url) {
        return parsed.url;
    }

    const { digioId, mail, tokenId } = parsed;

    Cookies.set('digio_token', tokenId);

    const theme = encodeURIComponent(
        JSON.stringify({
            PRIMARY_COLOR: '#2979BF',
            FONT_FAMILY: '',
            FONT_URL: '',
            FONT_FORMAT: '',
        })
    );

    const baseUrl = getDigioGatewayBaseUrl();

    // Admin link flow: return to /rpd?token= (same whitelisted page as IPV — no /digio-callback)
    if (adminToken) {
        const redirectParams = new URLSearchParams({
            token: adminToken,
            digio_return: '1',
        });

        const redirectUrl = encodeURIComponent(
            `${window.location.origin}/rpd?${redirectParams.toString()}`
        );

        return `${baseUrl}/${digioId}/${tokenId}/${mail}?token_id=${tokenId}&sdkver=11.0&method=otp&redirect_timeout=2800&theme=${theme}&redirect_url=${redirectUrl}`;
    }

    const redirectParams = new URLSearchParams({
        'verification-type': 'rpd',
        kycFlow: String(kycFlow),
    });

    const redirectUrl = encodeURIComponent(
        `${window.location.origin}/digio-callback?${redirectParams.toString()}`
    );

    return `${baseUrl}/${digioId}/${tokenId}/${mail}?token_id=${tokenId}&sdkver=11.0&method=otp&redirect_timeout=2800&theme=${theme}&redirect_url=${redirectUrl}`;
};

export const openDigioRpd = (data, options = {}) => {
    // In-app KYC/profile flows still use /digio-callback
    if (!options.adminToken) {
        setRpdPollPending();
    }

    window.location.href = buildDigioRpdUrl(data, options);
};

export const pollRpdCallback = (useOrderApi = false) => {
    if (useOrderApi === true) {
        return OrderAPIService(true, 'kyc/rpd/callback', {}, false, true);
    }

    return APIService(true, 'kyc/rpd/callback', JSON.stringify({}));
};

/**
 * Post-RPD redirect — mirrors IPV behaviour:
 * - Admin link flow (useOrderApi): logged in → `/`, logged out → landing page
 * - In-app KYC flow: → `/kyc` (same as digio-callback IPV; App handles auth/unlock)
 */
export const getRpdReturnUrl = (kycFlow = false, useOrderApi = false) => {
    if (kycFlow !== true) {
        return '/bank-account';
    }

    if (useOrderApi === true) {
        const refreshToken = Cookies.get('refreshToken');
        const landingPageUrl = process.env.REACT_APP_LANDING_PAGE_URL || 'https://mt-landing-staging.evoqins.dev';

        if (refreshToken === null || refreshToken === undefined) {
            return landingPageUrl;
        }

        return '/';
    }

    return '/kyc';
};

export const pollRpdUntilComplete = ({
    kycFlow = false,
    useOrderApi = false,
    onSuccess,
    onFailure,
    intervalMs = 3000,
    maxAttempts = 200,
}) => {
    let attempts = 0;
    let timerId = null;
    let stopped = false;

    const stop = () => {
        stopped = true;
        if (timerId) {
            clearInterval(timerId);
        }
    };

    const handleResponse = (response) => {
        if (stopped) {
            return;
        }

        if (response?.status_code === 200) {
            stop();
            onSuccess(response);
            return;
        }

        if (response?.status_code === 400) {
            const message = getRpdPendingMessage(response);

            if (message.includes('already exists')) {
                stop();
                onSuccess(response);
                return;
            }

            if (isRpdTerminalFailure(response)) {
                stop();
                onFailure(response);
                return;
            }

            return;
        }

        if (response?.status_code === 401) {
            return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
            stop();
            onFailure({
                message: 'Bank verification timed out. Please try again.',
                status_code: 408,
            });
        }
    };

    const poll = () => {
        pollRpdCallback(useOrderApi)
            .then(handleResponse)
            .catch(() => {
                attempts += 1;
                if (attempts >= maxAttempts) {
                    stop();
                    onFailure({
                        message: 'Unable to verify bank status. Please try again.',
                        status_code: 500,
                    });
                }
            });
    };

    poll();
    timerId = setInterval(poll, intervalMs);
    return stop;
};
